import React, { useState, useEffect } from 'react';
import { Typography } from '@/UIlibrary/Typography/Typography';
import { useTranslations } from 'use-intl';
import { toTranslationKey, tForComponent } from '@/utils/translations';
import QuestionnaireInput from './QuestionnaireInput';
import { formatValue } from './utils';
import EmptySection from './EmptySection';
import { useUpdateQuestionnaireStore } from '@/stores/useUpdateQuestionnaire';
import {
	MonthlyBusinessExpenses as MonthlyBusinessExpensesType,
	Maybe
} from '@/graphql/__generated__/credit-requests';
import QuestionnaireEditableInput from './QuestionnaireEditableInput';
interface MonthlyBusinessExpensesProps {
	data: Maybe<MonthlyBusinessExpensesType> | undefined;
	title: string;
	isEditing?: boolean;
	inputErrors?: Record<string, string>;
}

enum inputErrorMapping {
	rent = 'rent',
	electricBill = 'electric_bill',
	internetBill = 'internet_bill',
	equipmentMaintenance = 'equipment_maintenance',
	inputsPurchase = 'inputs_purchase',
	salaries = 'salaries',
	gas = 'gas'
}

const MonthlyBusinessExpenses: React.FC<MonthlyBusinessExpensesProps> = ({
	data,
	title,
	isEditing,
	inputErrors
}) => {
	const t = useTranslations('MicrocreditRequestQuestionnaire');
	const { monthlyBusinessExpenses, updateMonthlyBusinessExpenses } = useUpdateQuestionnaireStore();
	const [formData, setFormData] = useState(monthlyBusinessExpenses as MonthlyBusinessExpensesType);

	useEffect(() => {
		setFormData(monthlyBusinessExpenses);
	}, [monthlyBusinessExpenses]);

	const handleChange = (key: string, value: any) => {
		setFormData((prevData: any) => ({
			...prevData,
			[key]: value
		}));
		updateMonthlyBusinessExpenses({ [key]: value });
	};

	const editableFields = () => {
		return (
			<>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'rent'}
						value={formData['rent'] || data?.rent || ''}
						onChange={(newValue) => handleChange('rent', newValue)}
						error={inputErrors?.[inputErrorMapping.rent] || ''}
						type="money"
					/>
				</div>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'electricBill'}
						value={formData['electricBill'] || data?.electricBill || ''}
						onChange={(newValue) => handleChange('electricBill', newValue)}
						error={inputErrors?.[inputErrorMapping.electricBill] || ''}
						type="money"
					/>
				</div>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'internetBill'}
						value={formData['internetBill'] || data?.internetBill || ''}
						onChange={(newValue) => handleChange('internetBill', newValue)}
						error={inputErrors?.[inputErrorMapping.internetBill] || ''}
						type="money"
					/>
				</div>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'equipmentMaintenance'}
						value={formData['equipmentMaintenance'] || data?.equipmentMaintenance || ''}
						onChange={(newValue) => handleChange('equipmentMaintenance', newValue)}
						error={inputErrors?.[inputErrorMapping.equipmentMaintenance] || ''}
						type="money"
					/>
				</div>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'inputsPurchase'}
						value={formData['inputsPurchase'] || data?.inputsPurchase || ''}
						onChange={(newValue) => handleChange('inputsPurchase', newValue)}
						error={inputErrors?.[inputErrorMapping.inputsPurchase] || ''}
						type="money"
					/>
				</div>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'salaries'}
						value={formData['salaries'] || data?.salaries || ''}
						onChange={(newValue) => handleChange('salaries', newValue)}
						error={inputErrors?.[inputErrorMapping.salaries] || ''}
						type="money"
					/>
				</div>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'gas'}
						value={formData['gas'] || data?.gas || ''}
						onChange={(newValue) => handleChange('gas', newValue)}
						error={inputErrors?.[inputErrorMapping.gas] || ''}
						type="money"
					/>
				</div>
			</>
		);
	};

	return (
		<div className="flex flex-col gap-1 p-4 bg-white rounded-lg w-full">
			<div className={`flex flex-row items-center mb-4`}>
				<div className="grow">
					<Typography weight="semibold">{t(toTranslationKey(title))}</Typography>
				</div>
			</div>
			{data ? (
				<div className="flex flex-row w-full">
					<div className="flex flex-row flex-wrap w-full">
						{isEditing ? (
							editableFields()
						) : (
							<>
								<div className="w-1/2 px-3 py-1">
									<QuestionnaireInput title={'rent'} value={formatValue(data.rent, t)} />
								</div>
								<div className="w-1/2 px-3 py-1">
									<QuestionnaireInput
										title={'electricBill'}
										value={formatValue(data.electricBill, t)}
									/>
								</div>
								<div className="w-1/2 px-3 py-1">
									<QuestionnaireInput
										title={'internetBill'}
										value={formatValue(data.internetBill, t)}
									/>
								</div>
								<div className="w-1/2 px-3 py-1">
									<QuestionnaireInput
										title={'equipmentMaintenance'}
										value={formatValue(data.equipmentMaintenance, t)}
									/>
								</div>
								<div className="w-1/2 px-3 py-1">
									<QuestionnaireInput
										title={'inputsPurchase'}
										value={formatValue(data.inputsPurchase, t)}
									/>
								</div>
								<div className="w-1/2 px-3 py-1">
									<QuestionnaireInput title={'salaries'} value={formatValue(data.salaries, t)} />
								</div>
								<div className="w-1/2 px-3 py-1">
									<QuestionnaireInput title={'gas'} value={formatValue(data.gas, t)} />
								</div>
								<div className="w-1/2 px-3 py-1">
									<QuestionnaireInput
										title={'totalBusinessExpenses'}
										value={formatValue(data.totalBusinessExpenses, t)}
									/>
								</div>
							</>
						)}
					</div>
				</div>
			) : (
				<EmptySection t={tForComponent(t)} />
			)}
		</div>
	);
};

export default MonthlyBusinessExpenses;
